import {
  DestroyRef,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  signal,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import {
  type BrnCalendarI18n,
  type MonthLabels,
  provideBrnCalendarI18n,
  type Weekday,
} from '@spartan-ng/brain/calendar';

/**
 * Everything Spartan's calendar reads from its i18n service except `years`,
 * which is a plain number range with no language in it.
 */
export type CalendarI18n = Omit<BrnCalendarI18n, 'years'>;

export interface CalendarI18nOptions {
  /**
   * Active panel language. Read inside every text function, so a calendar that
   * is already open re-renders when it changes. `undefined` lets `Intl` use the
   * browser locale.
   */
  lang: Signal<string | undefined>;
  /** First day of the week, 0 (Sunday) to 6 (Saturday). Declared per project, never derived from the language. */
  weekStartsOn: Weekday;
}

/**
 * Builds the texts of Spartan's calendar from `Intl`, in the active panel
 * language. Weekday 0 is Sunday, month 0 is January — the same indices
 * Spartan passes in.
 *
 * Dates are built and formatted in UTC so the machine time zone never moves a
 * weekday or a month to its neighbour.
 */
export function buildCalendarI18n({ lang, weekStartsOn }: CalendarI18nOptions): CalendarI18n {
  const formatUtc = (date: Date, options: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat(lang(), { ...options, timeZone: 'UTC' }).format(date);
  // 2024-01-07 is a Sunday.
  const weekday = (index: number): Date => new Date(Date.UTC(2024, 0, 7 + index));
  const month = (index: number, year = 2000): Date => new Date(Date.UTC(year, index, 1));
  const relativeMonth = (offset: -1 | 1): string =>
    new Intl.RelativeTimeFormat(lang(), { numeric: 'auto' }).format(offset, 'month');

  return {
    formatWeekdayName: (index) => formatUtc(weekday(index), { weekday: 'short' }),
    labelWeekday: (index) => formatUtc(weekday(index), { weekday: 'long' }),
    months: () =>
      Array.from({ length: 12 }, (_, index) =>
        formatUtc(month(index), { month: 'long' }),
      ) as MonthLabels,
    formatMonth: (index) => formatUtc(month(index), { month: 'short' }),
    formatYear: (year) => formatUtc(month(0, year), { year: 'numeric' }),
    formatHeader: (index, year) =>
      formatUtc(month(index, year), { month: 'long', year: 'numeric' }),
    labelPrevious: () => relativeMonth(-1),
    labelNext: () => relativeMonth(1),
    firstDayOfWeek: () => weekStartsOn,
  };
}

/**
 * The single Spartan calendar i18n registration of the application, register
 * it once in `app.config.ts`. Every `hlm-calendar` — so `au-date-picker` and
 * `au-datetime-picker` — injects it; no component configures the calendar.
 *
 * Follows the Transloco active language, which changes without a reload.
 * `Intl` needs no translation file, so it does not wait for one to load —
 * this is why `langChanges$` is safe here even though the `transloco-i18n`
 * skill rules it out for texts that come from JSON.
 */
export function provideCalendarI18n(weekStartsOn: Weekday): EnvironmentProviders {
  const lang = signal<string | undefined>(undefined);

  return makeEnvironmentProviders([
    provideBrnCalendarI18n(buildCalendarI18n({ lang, weekStartsOn })),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      lang.set(transloco.getActiveLang());
      transloco.langChanges$
        .pipe(takeUntilDestroyed(inject(DestroyRef)))
        .subscribe((active) => lang.set(active));
    }),
  ]);
}
